import httpObject from './axios'
import primeConstants from './primeConstants'

let initPromise = null
let responseInterceptorId = null

export default {
  namespaced: true,
  state: {
    AppActiveAdmin: null,
    isLoggedIn: false,
    permissions: [],
    isHydrated: false,
  },
  mutations: {
    UPDATE_ADMIN_INFO(state, payload) {
      state.AppActiveAdmin = payload
      state.isLoggedIn = true
      state.permissions = Array.isArray(payload.permissions)
        ? payload.permissions
        : []
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions
    },
    SET_AUTH_HYDRATED(state, value) {
      state.isHydrated = value
    },
    CLEAR_ADMIN_INFO(state) {
      state.AppActiveAdmin = null
      state.isLoggedIn = false
      state.permissions = []
      state.isHydrated = false
    },
  },
  actions: {
    async init({ dispatch, commit, state }) {
      // 1. If an interceptor already exists, remove it safely
      if (responseInterceptorId !== null) {
        httpObject.interceptors.response.eject(responseInterceptorId)
      }

      // 2. Register the "One True Interceptor"
      responseInterceptorId = httpObject.interceptors.response.use(
        response => response,
        error => {
          const status = error?.response?.status

          if (status === 401) {
            dispatch('logout')
          }

          if (this.$app) {
            this.$app.hideLoading()
          }
          return Promise.reject(error)
        },
      )

      // CHECK FOR EXISTING DATA OR ACTIVE REQUESTS
      if (state.isHydrated) {
        return Promise.resolve()
      }

      if (initPromise) {
        return initPromise // Return the existing promise to prevent double calls
      }

      // START INITIALIZATION
      let userInfo = null
      try {
        const raw = localStorage.getItem(primeConstants.LOCAL_STORAGE_USER_KEY)
        const parsed = JSON.parse(raw)
        // Check if it's actually an object and not an array/null
        userInfo = (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
          ? parsed
          : null

        if (!userInfo && raw) {
          localStorage.removeItem(primeConstants.LOCAL_STORAGE_USER_KEY)
        }
      } catch (_error) {
        localStorage.removeItem(primeConstants.LOCAL_STORAGE_USER_KEY)
      }

      const apiToken = localStorage.getItem(primeConstants.LOCAL_STORE_API_TOKEN)

      if (apiToken) {
        if (userInfo) {
          commit('UPDATE_ADMIN_INFO', userInfo)
        }
        httpObject.updateAuthentication(apiToken)

        // Capture the fetch call in our traffic light variable
        initPromise = dispatch('fetchCurrentUser')
          .then(() => commit('SET_AUTH_HYDRATED', true))
          .catch(error => {
            if (error.response?.status === 401) {
              httpObject.removeAuthentication()
              localStorage.removeItem(primeConstants.LOCAL_STORAGE_USER_KEY)
              localStorage.removeItem(primeConstants.LOCAL_STORE_API_TOKEN)
            }
            commit('CLEAR_ADMIN_INFO')
            throw error
          })
          .finally(() => {
            initPromise = null // Reset once finished
          })

        return initPromise
      }

      commit('SET_AUTH_HYDRATED', true)
      return Promise.resolve()
    },
    async fetchCurrentUser({ commit }) {
      try {
        const response = await httpObject.get('/api/me')
        const { user, permissions } = response.data
        const adminData = { ...user, permissions }

        commit('UPDATE_ADMIN_INFO', adminData)
        localStorage.setItem(primeConstants.LOCAL_STORAGE_USER_KEY, JSON.stringify(adminData))

        return response.data
      } catch (error) {
        console.error('Failed to fetch user permissions', error)
        throw error // Propagate to router guard catch block
      }
    },
    updateApiToken(apiToken) {
      localStorage.setItem(primeConstants.LOCAL_STORE_API_TOKEN, apiToken)
      httpObject.updateAuthentication(apiToken)
    },
    logout({ commit, getters }) {
      if (getters.isLoggedIn) {
        commit('CLEAR_ADMIN_INFO')
        httpObject.post('/api/auth/logout')
        httpObject.removeAuthentication()
        localStorage.removeItem(primeConstants.LOCAL_STORAGE_USER_KEY)
        localStorage.removeItem(primeConstants.LOCAL_STORE_API_TOKEN)
        this.$app.$router.push({ path: '/pages/login' })
      }
    },
    async login({ commit }, payload) {
      const apiToken = payload.accessToken
      const adminData = {
        ...payload.user,
        permissions: payload.permissions,
      }

      // Set the token first so axios is ready
      httpObject.updateAuthentication(apiToken)
      localStorage.setItem(primeConstants.LOCAL_STORE_API_TOKEN, apiToken)

      // Commit and Save
      commit('UPDATE_ADMIN_INFO', adminData)
      localStorage.setItem(primeConstants.LOCAL_STORAGE_USER_KEY, JSON.stringify(adminData))

      this.$app.$router.push({ path: '/' })
    },
    updateProfile({ commit, state }, updatedData) {
      const newProfile = { ...state.AppActiveAdmin, ...updatedData }

      commit('UPDATE_ADMIN_INFO', newProfile)
      localStorage.setItem(primeConstants.LOCAL_STORAGE_USER_KEY, JSON.stringify(newProfile))
    },
    updateAdminEmail({ commit, state }, newEmail) {
      if (!state.AppActiveAdmin) return

      // Create a new object that preserves EVERYTHING (roles, departments, etc.)
      // and only swaps the email.
      const updatedAdmin = {
        ...state.AppActiveAdmin,
        email: newEmail,
      }

      commit('UPDATE_ADMIN_INFO', updatedAdmin)
      localStorage.setItem(primeConstants.LOCAL_STORAGE_USER_KEY, JSON.stringify(updatedAdmin))
    },
  },
  getters: {
    isLoggedIn: state => state.isLoggedIn,
    appActiveUser: state => state.AppActiveAdmin,
    userPermissions: state => state.permissions,
    isOwner: state => !!(state.AppActiveAdmin && state.AppActiveAdmin.is_owner),
  },
}
